import { apply, give, ITEM_ICONS, take, toast } from '../core/effects';
import { itemUse } from '../content/registry';
import { bus } from '../core/bus';
import { S } from '../core/game';
import { WATER_MAX, CHORES, type ItemKey } from '../core/state';
import { m } from '../i18n';
import { sfx } from '../audio/sfx';
import { completeObjective, todaysObjectives } from '../content/days';
import { h } from './dom';
import { itemInfo } from '../i18n/labels';
import { isPanelOpen, openPanel, refreshPanel } from './panel';
import { buyItem } from '../content/lifeMoney';
import { life, needOrWant } from '../content/money';
import { openWallet } from './wallet';

const USE: Partial<Record<ItemKey, () => void>> = {
  tea: () => (sfx('brew'), apply({ energy: 12, bladder: 6, minutes: 3 })),
  orange: () => (sfx('pop'), apply({ energy: 10, minutes: 2 })),
  candy: () => (sfx('pop'), apply({ mood: 4 })),
  cookies: () => (sfx('pop'), apply({ energy: 8, mood: 6, minutes: 2 })),
  water: () => (sfx('water'), apply({ energy: 6, bladder: 8, minutes: 1 })),
  riceBall: () => (sfx('pop'), apply({ energy: 15, mood: 3, minutes: 3 })),
  juice: () => (sfx('water'), apply({ energy: 10, mood: 2, bladder: 6, minutes: 1 })),
  sandwich: () => (sfx('pop'), apply({ energy: 14, minutes: 3 })),
  cocoa: () => (sfx('brew'), apply({ energy: 12, mood: 4, bladder: 5, minutes: 2 })),
};

const BAG_ORDER: ItemKey[] = ['water', 'tea', 'juice', 'riceBall', 'orange', 'candy', 'cookies', 'cake', 'chiffon', 'strawberries', 'tomato', 'sunflower', 'umbrella', 'book', 'atlas', 'kite', 'kitten', 'letter', 'sandwich', 'cocoa', 'flowers', 'scarf', 'keychain', 'toy', 'postcard', 'pineappleCake', 'mochi', 'pebble', 'photo'];

export function openBag(): void {
  openPanel('bag', () => {
    const s = S();
    const t = m().bag;
    const rows = BAG_ORDER.filter((k) => s.bag[k] > 0 || k === 'water').map((k) => {
      const info = itemInfo(k);
      const count = k === 'water' ? `${s.bag.water}/${WATER_MAX}` : `×${s.bag[k]}`;
      const use = USE[k] ?? itemUse(k);
      return h(
        'div',
        { class: 'item-row' },
        h('span', { class: 'item-icon' }, ITEM_ICONS[k]),
        h('div', { class: 'item-info' }, h('b', null, `${info.name} `, h('span', { class: 'muted' }, count)), h('small', null, info.desc)),
        use
          ? h(
              'button',
              {
                class: 'btn small green',
                disabled: s.bag[k] < 1,
                onclick: () => {
                  if (!take(k)) return toast(m().bag.emptyBottle);
                  use();
                  toast(m().bag.used(info.name));
                  refreshPanel('bag');
                },
              },
              t.use,
            )
          : null,
      );
    });
    return {
      title: t.title,
      body: [
        h('div', { class: 'wallet' }, h('span', null, `⭐ ${s.stars}`), h('span', null, `🪙 ${s.coins}`), h('span', null, `🐷 ${s.money.piggy}`)),
        h('button', { class: 'btn small ghost wide life-bag-wallet', onclick: () => (sfx('click'), openWallet()) }, m().life.wallet.menu),
        rows.length ? rows : h('p', { class: 'hint' }, t.empty),
      ],
    };
  });
}

export type ShopKind = 'store' | 'cafe' | 'gift';
const STOCK: Record<ShopKind, { k: ItemKey; price: number }[]> = {
  store: [
    { k: 'water', price: 5 },
    { k: 'umbrella', price: 35 },
    { k: 'strawberries', price: 20 },
    { k: 'riceBall', price: 12 },
    { k: 'juice', price: 10 },
    { k: 'tea', price: 15 },
    { k: 'orange', price: 10 },
    { k: 'candy', price: 8 },
  ],
  cafe: [
    { k: 'cocoa', price: 15 },
    { k: 'sandwich', price: 18 },
    { k: 'cookies', price: 20 },
    { k: 'cake', price: 60 },
  ],
  gift: [
    { k: 'flowers', price: 30 },
    { k: 'scarf', price: 45 },
    { k: 'keychain', price: 15 },
    { k: 'toy', price: 25 },
  ],
};

/** Need / want badge for a shop row (consumer education, see content/money.ts). */
function nwBadge(k: ItemKey): HTMLElement {
  const kind = needOrWant(k);
  return h('span', { class: `life-nw ${kind}` }, kind === 'need' ? `🧺 ${m().life.need}` : `🎈 ${m().life.want}`);
}

export function openShop(kind: ShopKind = 'store'): void {
  const SHOP = STOCK[kind];
  const useGift = kind === 'gift';
  openPanel('shop', () => {
    const s = S();
    const t = kind === 'store' ? m().shop : { ...m().shop, ...m().places.shops[kind] };
    const gift = useGift ? life().gift : 0;
    return {
      title: t.title,
      body: [
        h('p', { class: 'hint' }, t.hint),
        h('div', { class: 'wallet' }, h('span', null, `🪙 ${s.coins}`), gift ? h('span', null, `🎁 ${gift}`) : null),
        SHOP.map(({ k, price }) => {
          const full = k === 'water' && s.bag.water >= WATER_MAX;
          return h(
            'div',
            { class: 'item-row' },
            h('span', { class: 'item-icon' }, ITEM_ICONS[k]),
            h('div', { class: 'item-info' }, h('b', null, itemInfo(k).name, ' ', nwBadge(k)), h('small', null, `${itemInfo(k).desc} · ×${s.bag[k]}`)),
            h(
              'button',
              {
                class: 'btn small gold',
                disabled: s.coins + gift < price || full,
                onclick: () => {
                  if (S().coins + (useGift ? life().gift : 0) < price) return toast(m().shop.poor);
                  buyItem(k, price, t.title, useGift, () => {
                    give(k, 1);
                    toast(m().shop.bought(itemInfo(k).name));
                    if (k === 'strawberries') completeObjective('strawberries');
                    refreshPanel('shop');
                  });
                },
              },
              `${price}🪙`,
            ),
          );
        }),
      ],
    };
  });
}

export function openToday(): void {
  openPanel('today', () => {
    const s = S();
    const t = m().objectives;
    const c = m().chores;
    return {
      title: `📋 ${t.title}`,
      body: [
        h(
          'div',
          { class: 'checklist' },
          todaysObjectives(s).map((o) => h('div', { class: `check${o.done ? ' done' : ''}` }, h('span', { class: 'box' }, o.done ? '✓' : ''), h('span', null, o.text))),
        ),
        h('h4', null, `🏠 ${c.title}`),
        h('p', { class: 'hint' }, c.hint),
        h(
          'div',
          { class: 'checklist' },
          CHORES.map((k) => h('div', { class: `check${s.chores[k] ? ' done' : ''}` }, h('span', { class: 'box' }, s.chores[k] ? '✓' : ''), h('span', null, c[k]))),
        ),
      ],
    };
  });
}

// Keep open inventory panels in sync with the game state.
bus.on('change', () => {
  for (const id of ['bag', 'shop', 'today']) if (isPanelOpen(id)) refreshPanel(id);
});
